/**
 * Cliente HTTP base con manejo de errores.
 * Usa fetch nativo para realizar peticiones al backend.
 */

// =====================================================
// MANEJO DE TOKENS EN LOCALSTORAGE
// =====================================================

const TOKEN_KEY = 'vacation_token';
const REFRESH_TOKEN_KEY = 'vacation_refresh_token';

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setStoredTokens(token: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearStoredTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// =====================================================
// HELPER DE HEADERS CON AUTHORIZATION
// =====================================================

function getAuthHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

// =====================================================
// FUNCIONES HTTP
// =====================================================

export async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options?.headers,
    },
  });

  if (response.status === 401) {
    clearStoredTokens();
    window.location.href = '/login';
    throw new Error('Sesión expirada');
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Error desconocido');
    throw new Error(
      `Error HTTP ${response.status}: ${response.statusText}. ${errorText}`
    );
  }

  return response.json() as Promise<T>;
}

/**
 * Realiza una petición POST enviando JSON.
 */
export async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401) {
    clearStoredTokens();
    window.location.href = '/login';
    throw new Error('Sesión expirada');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
    throw new Error(errorData.message || `Error HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Realiza una petición PUT enviando JSON.
 */
export async function putJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401) {
    clearStoredTokens();
    window.location.href = '/login';
    throw new Error('Sesión expirada');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
    throw new Error(errorData.message || `Error HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Realiza una petición DELETE.
 */
export async function deleteRequest(url: string): Promise<void> {
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (response.status === 401) {
    clearStoredTokens();
    window.location.href = '/login';
    throw new Error('Sesión expirada');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
    throw new Error(errorData.message || `Error HTTP ${response.status}`);
  }
}
